import {
  FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SvgTelegram } from "../../../assets/icons/SvgTelegram";
import { Button } from "../../../common/components/Button/Button";
import { Input } from "../../../common/components/Input/Input";
import { QueryLayout } from "../../../common/components/Layout/QueryLayout";
import { ISupportMessage } from "../../../common/models/support/ISupport";
import { getErrorMessage, timeFormatter } from "../../../common/utils/helpers";
import {
  useGetSupportMessagesQuery,
  useLazyGetSupportMessagesBeforeQuery,
  useMarkSupportTicketReadMutation,
  useMarkSupportVoiceListenedMutation,
  useReopenSupportTicketMutation,
  useSendSupportMessageMutation,
} from "../../../services/supportApi";
import { SupportMessageBubble } from "./SupportMessageBubble";

interface ISupportChatProps {
  ticketId: number;
  isFinished: boolean;
}

/** Отступ от низа ленты, в пределах которого новые сообщения подкручивают её вниз */
const STICK_TO_BOTTOM_PX = 80;

const MAX_MESSAGE_LENGTH = 4000;

function newClientId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Переписка с пользователем. Ответить можно только текстом: записи голосовых в
 * панели нет (ограничение интерфейса для менеджеров), голосовые пользователя
 * проигрываются. Лента сквозная по обращениям пользователя, границы обращений
 * отмечены разделителями. Рассчитана на родительскую сетку из двух строк:
 * лента и нижняя панель.
 */
export const SupportChat: FC<ISupportChatProps> = memo(
  ({ ticketId, isFinished }) => {
    const [draft, setDraft] = useState("");
    const [sendError, setSendError] = useState<string>();
    const [older, setOlder] = useState<ISupportMessage[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState<boolean>();

    const scrollRef = useRef<HTMLDivElement>(null);
    const stickToBottom = useRef(true);
    const heightBeforePrepend = useRef<number>();
    const lastMarkedId = useRef(0);

    const { data, isLoading, error } = useGetSupportMessagesQuery(ticketId);
    const [loadOlder, { isFetching: isLoadingOlder }] =
      useLazyGetSupportMessagesBeforeQuery();
    const [sendMessage, { isLoading: isSending }] =
      useSendSupportMessageMutation();
    const [markRead] = useMarkSupportTicketReadMutation();
    const [markListened] = useMarkSupportVoiceListenedMutation();
    const [reopenTicket, { isLoading: isReopening }] =
      useReopenSupportTicketMutation();

    const messages = useMemo(() => {
      const byId = new Map<number, ISupportMessage>();
      [...older, ...(data?.messages ?? [])].forEach((message) =>
        byId.set(message.id, message)
      );
      return Array.from(byId.values()).sort((a, b) => a.id - b.id);
    }, [older, data]);

    const hasMore = hasMoreOlder ?? data?.has_more ?? false;
    const lastMessageId = messages.length
      ? messages[messages.length - 1].id
      : 0;

    // Новое сообщение (своё или пришедшее) подкручивает ленту, если её не листали вверх
    useLayoutEffect(() => {
      const el = scrollRef.current;
      if (el && stickToBottom.current) {
        el.scrollTop = el.scrollHeight;
      }
    }, [lastMessageId, isLoading]);

    // Подгрузка старых сообщений сверху не должна сдвигать то, что читает менеджер
    useLayoutEffect(() => {
      const el = scrollRef.current;
      if (el && heightBeforePrepend.current !== undefined) {
        el.scrollTop += el.scrollHeight - heightBeforePrepend.current;
        heightBeforePrepend.current = undefined;
      }
    }, [older]);

    // Открытая переписка прочитана: сбрасываем счётчик непрочитанного в списке
    const newestUnreadId = useMemo(
      () =>
        messages.reduce(
          (max, m) =>
            m.ticket_id === ticketId && m.sender === "user" && !m.read_at
              ? Math.max(max, m.id)
              : max,
          0
        ),
      [messages, ticketId]
    );

    useEffect(() => {
      if (newestUnreadId > lastMarkedId.current) {
        lastMarkedId.current = newestUnreadId;
        markRead(ticketId);
      }
    }, [newestUnreadId, ticketId, markRead]);

    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      stickToBottom.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
    };

    const handleLoadOlder = async () => {
      if (!messages.length || isLoadingOlder) return;

      const result = await loadOlder({ ticketId, beforeId: messages[0].id });
      if (!result.data) return;

      heightBeforePrepend.current = scrollRef.current?.scrollHeight;
      setOlder((current) => [...result.data!.messages, ...current]);
      setHasMoreOlder(result.data.has_more);
    };

    const handleSend = async () => {
      const text = draft.trim();
      if (!text || isSending) return;

      setSendError(undefined);
      setDraft("");
      stickToBottom.current = true;

      const result = await sendMessage({
        ticketId,
        text,
        clientId: newClientId(),
      });
      if ("error" in result) {
        setDraft(text);
        setSendError(
          getErrorMessage(result.error)?.data?.message ||
            "Не удалось отправить сообщение"
        );
      }
    };

    const handleVoiceFirstPlay = useCallback(
      (message: ISupportMessage) => {
        markListened({ messageId: message.id, ticketId });
      },
      [markListened, ticketId]
    );

    return (
      <>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="mb-[10px] overflow-y-scroll"
        >
          <QueryLayout isLoading={isLoading} error={error}>
            {hasMore && (
              <div className="mb-[15px] flex justify-center">
                <Button
                  mode="darkBorder"
                  className="py-[5px] px-[15px] text-sm"
                  disabled={isLoadingOlder}
                  onClick={handleLoadOlder}
                >
                  Показать более ранние
                </Button>
              </div>
            )}
            {messages.map((message, index) => (
              <div key={message.id}>
                {(index === 0 ||
                  messages[index - 1].ticket_id !== message.ticket_id) && (
                  <p className="mb-[12px] text-center text-xs text-lightGray">
                    Обращение № {message.ticket_id},{" "}
                    {timeFormatter({
                      time: new Date(message.created_at),
                      mode: "dd-Mth-yyyy",
                    })}
                  </p>
                )}
                <SupportMessageBubble
                  message={message}
                  onVoiceFirstPlay={handleVoiceFirstPlay}
                />
              </div>
            ))}
          </QueryLayout>
        </div>
        <div className="relative mx-[5px] mb-[15px] flex items-center rounded p-[15px] pr-0 shadow">
          {sendError && (
            <p className="absolute -top-[16px] left-[5px] text-xs text-red">
              {sendError}
            </p>
          )}
          {isFinished ? (
            <>
              <p className="mr-[10px] flex-1 text-sm font-medium text-gray">
                Обращение закрыто
              </p>
              <Button
                className="mr-[15px] max-h-[35px] py-[5px]"
                disabled={isReopening}
                onClick={() => reopenTicket(ticketId)}
              >
                Открыть заново
              </Button>
            </>
          ) : (
            <>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                invalid={!!sendError}
                maxLength={MAX_MESSAGE_LENGTH}
                className="h-[35px] w-full text-[12px]"
                placeholder="Сообщение"
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSend();
                }}
              />
              <button
                onClick={handleSend}
                disabled={!draft.trim() || isSending}
                aria-label="Отправить"
                className="flex h-full w-[60px] items-center justify-center text-black transition hover:text-primary focus:text-primary active:text-black disabled:text-gray"
              >
                <SvgTelegram className="h-[22px] w-[22px]" />
              </button>
            </>
          )}
        </div>
      </>
    );
  }
);
